import { useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import {
  EditTaskItemNoteDto,
  FetchError,
  GetUserTaskItemsDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  TaskItemDetailDto,
  useAccessToken,
  useTaskItemMutation,
} from "src/http";

type Variables = {
  taskItemGuid: string;
  note: string;
  teamSlug?: string;
  ownerUserGuids: string[];
};

const useEditTaskItemNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemNoteRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditTaskItemNoteDto = {
      note: variables.note,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}/note`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemNoteRequest };
};

const useEditTaskItemNote = () => {
  const { editTaskItemNoteRequest } = useEditTaskItemNoteRequest();
  const queryClient = useQueryClient();

  return useTaskItemMutation({
    mutationFn: editTaskItemNoteRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;
      let cachedUserData: { [userGuid: string]: GetUserTaskItemsDto | undefined } = {};

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        () => ({
          note: variables.note,
        })
      );

      if (variables.ownerUserGuids) {
        variables.ownerUserGuids.forEach(async (userGuid) => {
          cachedUserData[userGuid] = await optimisticallyUpdateQueryData<GetUserTaskItemsDto>(
            queryClient,
            queryKeys.taskItems.getUserTaskItems(userGuid),
            (cachedData) => ({
              taskItems: cachedData.taskItems.map((taskItem) => {
                if (taskItem.guid === variables.taskItemGuid) {
                  return { ...taskItem, note: variables.note };
                } else {
                  return taskItem;
                }
              }),
            })
          );
        });
      }

      return {
        cachedDetailData,
        cachedUserData,
      };
    },
  });
};

export { useEditTaskItemNote };
